import { BrowserModule } from "@angular/platform-browser";
import { NgModule } from "@angular/core";
import {
  LocationStrategy,
  PathLocationStrategy,
  CommonModule,
} from "@angular/common";
import { ScrollEventModule } from "ngx-scroll-event";
import { AppComponent } from "./app.component";
import { ScrollToModule } from "ng2-scroll-to";
// Import containers
import { FullLayout, SimpleLayout } from "./containers";
// Import components
import {
  AppAside,
  AppBreadcrumbs,
  AppFooter,
  AppHeader,
  AppSidebar,
  AppSidebarFooter,
  AppSidebarForm,
  AppSidebarHeader,
  AppSidebarMinimizer,
} from "./components";
// Import directives
import {
  AsideToggleDirective,
  NAV_DROPDOWN_DIRECTIVES,
  SIDEBAR_TOGGLE_DIRECTIVES,
} from "./directives";
// Import routing module
import { AppRoutingModule } from "./app.routing";
// Import 3rd party components
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { TabsModule } from "ngx-bootstrap/tabs";
import { ChartsModule } from "ng2-charts/ng2-charts";
import { HttpModule, XHRBackend, RequestOptions } from "@angular/http";
import { AuthenticationService } from "./_services/authentication.service";
import { ServerURLInterceptor } from "./_models/interceptor";
import { InterceptorService } from "ng2-interceptors";
import { interceptorFactory } from "./views/auth/auth.module";
import { AuthGuard } from "./_guards/auth.guard";
import { AuthAdminGuard } from "./_guards/auth-admin.guard";
import { AuthProjectGuard } from "./_guards/auth-project.guard";
import { SmmService } from "./_services/smm.service";
import { SmmPostService } from "./_services/smm-post.service";
import { SmmCommentService } from "./_services/smm-comment.service";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { CollapseModule, ModalModule } from "ngx-bootstrap";
import { DateTimePickerModule } from "ng-pick-datetime";
import { FacebookModule } from "ngx-facebook";
import { LaddaModule } from "angular2-ladda";
import { ToastrModule, ToastrService } from "ngx-toastr";
import { AccountsService } from "./_services/accounts.service";
import { FollowService } from "./_services/follow.service";
import { DepartmentService } from "./_services/department.service";
import { RoleService } from "./_services/role.service";
import { ProjectsService } from "./_services/projects.service";
import { GlobalService } from "./_services/global.service";
import { AccountService } from "./_services/account.service";
import { SmmPageService } from "./_services/smm-page.service";
import { SmmGroupService } from "./_services/smm-group.service";
import { SmmUserService } from "./_services/smm-user.service";
import { BsModalService } from "ngx-bootstrap/modal";
import { PagesModule } from "./views/pages/pages.module";
import { CommentTmpService } from "./_services/comment-tmp.service";
import { PopupModalComponent } from "./views/system/departments/popup-modal.component";
import { PermissionModule } from "./views/system/permission/permission.module";
import { FbUserSeedingModule } from "./views/fb-user-seeding/fb-user-seeding.module";
import { FbUsersService } from "./_services/fb-users.service";
import { CommentService } from "./_services/comment.service";
import { VPSHostingService } from "./_services/vps-hosting.service";
import { FbUserSeedingService } from "./_services/fb-user-seeding.service";
import { DashboardService } from "./_services/dashboard.service";
import { DataService } from "app/_custom/search-filter/search-filter.service";
import { AccessTokenService } from "./_services/access-token.service";
import { ReportService } from "./_services/report.service";
import {
  AuthScanAccountManagementGuard,
  AuthSeedingAccountManagerGuard,
  AuthVPSHostingManagementGuard,
  AuthPriorityListManagementGuard,
  AuthTokenManagementGuard,
  AuthNewsAnalyticConfigGuard,
} from "./_guards/auth-group-account-management.guard";
import {
  AuthGeneralReportGuard,
  AuthGeneralTopicReportGuard,
  AuthProcessByProjectGuard,
  AuthResponseRateReportGuard,
  AuthSeedingReportGuard,
  AuthViewDashboard2Guard,
  AuthViewDashboard1Guard,
  AuthViewDashboard4Guard,
  AuthViewDashboard3Guard,
} from "./_guards/auth-report.guard";
import { ErrorInterceptor } from "./_models/error.interceptor";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { RouterProjectService } from "./_services/router-project.service";
import { DiscussionProjectsService } from "./_services/discussionProjects.service";
import { DashboardsModule } from "./views/dashboards/dashboards.module";
import { ProvincesModule } from "./views/provinces/provinces.module";
import { DashboardFilterService } from "./views/dashboards/dashboard-filter/dashboard-filter.service";
import { PostService } from "./_services/post.service";
import { ProvinceService } from "./_services/province.service";
import { QuickResearchProjectsService } from "./_services/quickResearchProject.service";
import { ReportLoadingService } from "./_services/report-loading.service";
import { PostManagementModule } from "./views/post-management/post-management.module";
import { AutoSeedingModule } from "./views/auto-seeding/auto-seeding.module";
import { AuthPermissionGuard } from "./_guards/auth-permission.guard";
import { PostTmpsService } from "./_services/post-tmp.service";
import { IpServiceService } from "./_services/ip-service.service";
import { PostNegativeModule } from "./views/post-negative/post-negative.module";
import { BrandsModule } from "./views/brand-manage/brands.module";
import { ReportManagerModule } from "./views/report-manager/report-manager.module";
import { BrandDataSourceReportsModule } from "./views/reports-module/brand-data-source/brand-data-source-reports.module";
import { BrandReportsModule } from "./views/reports-module/brand-report/brand-reports.module";
import { SentimentReportsModule } from "./views/reports-module/sentiment-report/sentiment-reports.module";
import { TopContentDiscussModule } from "./views/reports-module/top-content-discuss/top-content-discuss.module";
import { DiscussionDevelopmentReportsModule } from "./views/reports-module/discussion-development/discussion-development-reports.module";
import { BrandComparisonReportsModule } from "./views/reports-module/brand-comparison/brand-comparison-reports.module";
import { FieldsService } from "./_services/fields.service";
import { OutstandingStatisticsModule } from "./views/reports-module/outstanding-statistics/outstanding-statistics.module";
import { HealthBrandModule } from "./views/reports-module/health-brand/health-brand.module";
import { ChannelDiscussionModule } from "./views/reports-module/channel-discussion/channel-discussion.module";
import { DiscussionMarketShareModule } from "./views/reports-module/discussion-market-share/discussion-market-share.module";
import { ComparePageAnalyticModule } from "./views/reports-module/compare-page-analytic/compare-page-analytic.module";
import { ComparePageAnalyticService } from "./views/reports-module/compare-page-analytic/compare-page-analytic.service";
import { LogSeedingService } from "./_services/log-seeding.service";
import { DiscussionTotalReportModule } from "./views/reports-module/discussion-total-report/discussion-total-report.module";
import { DiscussionTVSocialModule } from "./views/reports-module/discussion-tv-social/discussion-tv-social.module";
import { YoutubeService } from "./_services/youtube.service";
import { TiktokService } from "./_services/tiktok.service";
import { UserTimeReportService } from "./_services/user-time-report.service";

const APP_CONTAINERS = [FullLayout, SimpleLayout];

const APP_COMPONENTS = [
  AppAside,
  AppBreadcrumbs,
  AppFooter,
  AppHeader,
  AppSidebar,
  AppSidebarFooter,
  AppSidebarForm,
  AppSidebarHeader,
  AppSidebarMinimizer,
];

const APP_DIRECTIVES = [
  AsideToggleDirective,
  NAV_DROPDOWN_DIRECTIVES,
  SIDEBAR_TOGGLE_DIRECTIVES,
];

@NgModule({
  imports: [
    BrowserModule,
    HttpModule,
    AppRoutingModule,
    BsDropdownModule.forRoot(),
    TabsModule.forRoot(),
    FacebookModule.forRoot(),
    ChartsModule,
    ModalModule.forRoot(),
    DateTimePickerModule,
    BrowserAnimationsModule,
    CollapseModule.forRoot(),
    BsDropdownModule.forRoot(),
    ToastrModule.forRoot(),
    ToastrModule,
    LaddaModule,
    ScrollEventModule,
    ScrollToModule.forRoot(),
    PagesModule,
    PermissionModule,
    FbUserSeedingModule,
    DashboardsModule,
    ProvincesModule,
    PostManagementModule,
    PostNegativeModule,
    BrandsModule,
    ReportManagerModule,
    BrandReportsModule,
    BrandDataSourceReportsModule,
    SentimentReportsModule,
    TopContentDiscussModule,
    DiscussionDevelopmentReportsModule,
    BrandComparisonReportsModule,
    OutstandingStatisticsModule,
    ChannelDiscussionModule,
    HealthBrandModule,
    DiscussionMarketShareModule,
    ComparePageAnalyticModule,
    DiscussionTotalReportModule,
    AutoSeedingModule,
  ],
  declarations: [
    AppComponent,
    ...APP_CONTAINERS,
    ...APP_COMPONENTS,
    ...APP_DIRECTIVES,
    PopupModalComponent,
  ],
  exports: [PopupModalComponent],
  entryComponents: [PopupModalComponent],
  providers: [
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy,
    },
    AuthGuard,
    AuthAdminGuard,
    AuthProjectGuard,
    AuthScanAccountManagementGuard,
    AuthSeedingAccountManagerGuard,
    AuthVPSHostingManagementGuard,
    AuthPriorityListManagementGuard,
    AuthNewsAnalyticConfigGuard,
    AuthTokenManagementGuard,
    AuthGeneralReportGuard,
    AuthGeneralTopicReportGuard,
    AuthProcessByProjectGuard,
    AuthResponseRateReportGuard,
    AuthSeedingReportGuard,
    AuthenticationService,
    AuthViewDashboard2Guard,
    AuthViewDashboard1Guard,
    AuthViewDashboard3Guard,
    AuthViewDashboard4Guard,
    ServerURLInterceptor,
    AuthPermissionGuard,
    {
      provide: InterceptorService,
      useFactory: interceptorFactory,
      deps: [XHRBackend, RequestOptions, ServerURLInterceptor],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    SmmService,
    SmmPageService,
    SmmGroupService,
    SmmUserService,
    SmmPostService,
    SmmCommentService,
    AccountsService,
    FollowService,
    DepartmentService,
    ProjectsService,
    GlobalService,
    AccountService,
    ToastrService,
    BsModalService,
    DepartmentService,
    RoleService,
    CommentTmpService,
    RoleService,
    FbUsersService,
    CommentService,
    VPSHostingService,
    FbUserSeedingService,
    DataService,
    AccessTokenService,
    ReportService,
    RouterProjectService,
    DiscussionProjectsService,
    DashboardFilterService,
    DashboardService,
    PostService,
    ProvinceService,
    QuickResearchProjectsService,
    ReportLoadingService,
    PostTmpsService,
    IpServiceService,
    FieldsService,
    ComparePageAnalyticService,
    LogSeedingService,
    YoutubeService,
    TiktokService,
    UserTimeReportService,
  ],

  bootstrap: [AppComponent],
})
export class AppModule {}



// WEBPACK FOOTER //
// D:/Project/Nodejs/SocialListening_Cloud/frontend_cloud/src/app/app.module.ts